// src/utils/fxConvert.js
//
// LSE-TAB-REDESIGN Part 1 — pure FX conversion helper.
//
// Algorithm: rate_to_usd is the rate at which 1 USD == N <ccy>. So:
//   usdValue   = nativeAmount / rate_to_usd[fromCcy]
//   targetCcy  = usdValue * rate_to_usd[toCcy]
//
// `rates` is a flat map { USD: 1, GBP: 0.792, EUR: 0.920, ... } as
// produced by the GET /api/fx-rates response after the useFxRates
// hook flattens `[{currency, rateToUsd}, ...]` into the map shape.
//
// Why a pure helper (not a hook): the toggle re-renders many values
// per page; binding each value's conversion to a hook would explode
// the render graph. Hook owns the fetch; this owns the math.
//
// Returns null when:
//   - amount is null/undefined/NaN (caller renders "—" or NotSpecified)
//   - either currency is missing from the rates map (caller surfaces the
//     gap rather than silently displaying a wrong number — per
//     CLAUDE.md "errors are signals, not bugs; investigate before
//     suppressing")
//
// Same-currency short-circuit (fromCcy === toCcy) returns the input
// unchanged WITHOUT consulting the rates map, so a USD→USD render
// works even if /api/fx-rates is still loading.

export function convertAmount(amount, fromCcy, toCcy, rates) {
  if (amount == null) return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;

  if (!fromCcy || !toCcy) return null;
  if (fromCcy === toCcy) return n;

  if (!rates || typeof rates !== "object") return null;
  const fromRate = rates[fromCcy];
  const toRate   = rates[toCcy];
  if (!Number.isFinite(fromRate) || fromRate <= 0) return null;
  if (!Number.isFinite(toRate)   || toRate   <= 0) return null;

  const usd = n / fromRate;
  return usd * toRate;
}
